import {useState, useEffect, useCallback} from "react";

/**
 * This file defines the context sidebar positioning within a chat thread.
 * It provides an enum, default, and a hook to persist and read the sidebar's position (left or right).
 */

export enum SidebarPosition {
    Left = "left",
    Right = "right",
}

const SIDEBAR_POSITION_STORAGE_KEY = "context-sidebar-position";
const DEFAULT_SIDEBAR_POSITION = SidebarPosition.Right;

/**
 * Hook to manage context sidebar position (left/right) with localStorage persistence.
 * Handles both JSON-serialized (from useLocalStorage) and plain string formats for backwards compatibility.
 * This is a global setting for the entire app, not per-thread.
 */
export const useSidebarPosition = () => {
    // Read sidebar position from localStorage
    // Handles both JSON-serialized (from useLocalStorage) and plain string formats
    const getSidebarPosition = useCallback(() => {
        try {
            const saved = localStorage.getItem(SIDEBAR_POSITION_STORAGE_KEY);
            if (!saved) return DEFAULT_SIDEBAR_POSITION;

            // Try to parse as JSON (useLocalStorage uses JSON.stringify)
            let value: string;
            try {
                value = JSON.parse(saved);
            } catch {
                // If not JSON, use raw value (for backwards compatibility)
                value = saved.replace(/^["']|["']$/g, "");
            }

            return (
                value === SidebarPosition.Left ||
                value === SidebarPosition.Right
                    ? value
                    : DEFAULT_SIDEBAR_POSITION
            ) as SidebarPosition;
        } catch {
            return DEFAULT_SIDEBAR_POSITION;
        }
    }, []);

    const [sidebarPosition, setSidebarPosition] =
        useState<SidebarPosition>(getSidebarPosition);

    // Save position preference to localStorage whenever it changes
    // Use JSON.stringify to match useLocalStorage format from Settings
    useEffect(() => {
        try {
            localStorage.setItem(
                SIDEBAR_POSITION_STORAGE_KEY,
                JSON.stringify(sidebarPosition)
            );
        } catch {
            // Ignore localStorage errors
        }
    }, [sidebarPosition]);

    // Listen for localStorage changes from Settings page
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === SIDEBAR_POSITION_STORAGE_KEY && e.newValue) {
                const parsed = getSidebarPosition();
                setSidebarPosition(parsed);
            }
        };

        // Also check localStorage directly for same-tab updates (storage event only fires for other tabs)
        const checkStorage = () => {
            try {
                const saved = getSidebarPosition();
                setSidebarPosition(saved);
            } catch {
                // Ignore errors
            }
        };

        window.addEventListener("storage", handleStorageChange);
        // Check periodically for same-tab updates (when Settings page updates localStorage)
        const interval = setInterval(checkStorage, 500);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            clearInterval(interval);
        };
    }, [getSidebarPosition]);

    return [sidebarPosition, setSidebarPosition] as const;
};

// Export the storage key and default for use in other components
export {SIDEBAR_POSITION_STORAGE_KEY, DEFAULT_SIDEBAR_POSITION};
